import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS } from "../data/content";
import { PillButton, Logo } from "./PillButton";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 left-0 right-0 z-50"
      data-testid="navbar"
    >
      <div
        className={`transition-all duration-300 ${
          scrolled
            ? "bg-white/80 backdrop-blur-md border-b-2 border-black"
            : "bg-transparent"
        }`}
      >
        <div className="container-x flex items-center justify-between h-[74px]">
          <Link to="/" data-testid="nav-logo" aria-label="grooh home">
            <Logo />
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((l) => {
              const active = pathname === l.to;
              return (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-link-${l.label.toLowerCase()}`}
                  className={`px-4 py-2 rounded-full font-heading font-semibold text-[15px] transition-colors ${
                    active
                      ? "text-[#0033A2]"
                      : "text-black hover:text-[#0033A2]"
                  }`}
                >
                  {l.label}
                </Link>
              );
            })}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Link
              to="/contact"
              data-testid="nav-signin"
              className="font-heading font-semibold text-[15px] hover:text-[#0033A2] transition-colors"
            >
              Sign in
            </Link>
            <PillButton to="/contact" testid="nav-cta-demo" className="!py-2.5 !px-5">
              Book a demo <ArrowUpRight size={18} strokeWidth={2.5} />
            </PillButton>
          </div>

          <button
            className="lg:hidden w-11 h-11 flex items-center justify-center border-2 border-black rounded-full bg-white"
            onClick={() => setOpen((o) => !o)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} strokeWidth={2.5} /> : <Menu size={22} strokeWidth={2.5} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-white border-b-2 border-black overflow-hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="container-x py-6 flex flex-col gap-2">
              {NAV_LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  className="py-3 font-heading font-bold text-xl border-b border-black/10 flex items-center justify-between"
                >
                  {l.label} <ArrowUpRight size={20} />
                </Link>
              ))}
              <PillButton to="/contact" testid="nav-mobile-cta" className="mt-4 w-full">
                Book a demo <ArrowUpRight size={18} strokeWidth={2.5} />
              </PillButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
